import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { 
  FaChair, FaTruck, FaRecycle, FaFileInvoice, 
  FaCheckCircle, FaUtensils, FaClock, FaRecycle as FaRefresh,
  FaPrint, FaEye, FaTimes, FaSpinner,
   FaCalendarAlt, FaMoneyBillWave, FaShoppingBag,
  FaPhone, FaUser, FaTag
} from 'react-icons/fa';
import { Button, Modal } from 'react-bootstrap';

import PrintSaleInvoice from './PrintSaleInvoice';

const OrderManagement = ({ show, onHide, onOrderComplete }) => {

  
  const [orders, setOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [selectedOrderType, setSelectedOrderType] = useState('dining');
  const [orderCounts, setOrderCounts] = useState({
    dining: 0,
    delivery: 0,
    takeaway: 0
  });
  const [showConfirmComplete, setShowConfirmComplete] = useState(false);
  const [orderToComplete, setOrderToComplete] = useState(null);
  
  // Invoice Preview States
  const [showInvoicePreview, setShowInvoicePreview] = useState(false);
  const [invoiceData, setInvoiceData] = useState(null);
  const [invoiceLoading, setInvoiceLoading] = useState(false);
  
  // Print states
  const [printData, setPrintData] = useState(null);
  const [isPrint, setIsPrint] = useState(false);
  const [isPrinting, setIsPrinting] = useState(false);
  
  // Animation states for buttons
  const [animatingButton, setAnimatingButton] = useState(null);

  // Fetch orders when modal opens or filter changes
  useEffect(() => {
    if (show) {
      fetchOrders(selectedOrderType);
    }
  }, [show, selectedOrderType]);

  // Handle print effect - similar to AllSaleInvoices
  useEffect(() => {
    if (isPrint && printData) {
      const div = document.getElementById('printablearea-order');
      if (div) {
        const win = window.open('', '', 'height=700,width=700');
        if (win) {
          win.document.write(div.outerHTML);
          win.document.close();
          setTimeout(function() {
            win.print();
            win.close();
          }, 2000);
        }
      }
      setIsPrint(false);
      setIsPrinting(false);
      setPrintData(null);
    }
  }, [isPrint, printData]);

  const fetchOrders = async (type) => {
    setOrdersLoading(true);
    try {
      const response = await axios.get(`/api/all-sale-invoices?order_type=${type}&order_status=pending`);
      if (response.data.status === 200) {
        setOrders(response.data.invoices.data || []);
        if (response.data.order_counts) {
          setOrderCounts(response.data.order_counts);
        }
      }
    } catch (error) {
      console.error('Error fetching orders:', error);
      toast.error('Failed to load orders');
    } finally {
      setOrdersLoading(false);
    }
  };

  const handleOrderTypeFilter = (type) => {
    setAnimatingButton(type);
    setTimeout(() => setAnimatingButton(null), 500);
    setSelectedOrderType(type);
    fetchOrders(type);
  };

  const handleCompleteOrder = (order) => {
    setOrderToComplete(order);
    setShowConfirmComplete(true);
  };

  const confirmCompleteOrder = async () => {
    if (!orderToComplete) return;
    
    setAnimatingButton('confirming');
    try {
      const response = await axios.post('/api/update-order-status', {
        invoice_id: orderToComplete.id,
        order_status: 'completed'
      });

      if (response.data.status === 200) {
        toast.success('Order completed successfully!');
        setShowConfirmComplete(false);
        setOrderToComplete(null);
        fetchOrders(selectedOrderType);
        if (onOrderComplete) {
          onOrderComplete(orderToComplete);
        }
      }
    } catch (error) {
      console.error('Error confirming order:', error);
      toast.error('Failed to confirm order');
    } finally {
      setAnimatingButton(null);
    }
  };

  // Function to fetch and format invoice data
  const fetchInvoiceData = async (order) => {
    try {
      const invoiceId = order.visitor_id || order.id;
      const response = await axios.post('/api/reinvoice/print', { id: invoiceId });
      
      if (response.data) {
        const apiData = response.data;
        const items = apiData.cart || apiData.letter || [];
        
        const formattedItems = items.map(item => ({
          ...item,
          qty: item.qty || 1,
          unitsaleprice: item.unitsaleprice || 0,
          subtotaldiscount: item.subtotaldiscount || 0,
          discountpercent: item.discountpercent || 0,
          weight: item.weight || 0,
          unit: item.unit || '',
          pack_size: item.pack_size || 1,
        }));

        const formattedInvoiceData = {
          letter: formattedItems,
          dates: apiData.dates || new Date().toISOString().split('T')[0],
          salestax: apiData.salestax || 0,
          shipping: apiData.shipping || 0,
          discount: apiData.discount || 0,
          grandtotal: apiData.grandtotal || 0,
          remainingbill: apiData.remainingbill || 0,
          entercash: apiData.entercash || 0,
          recovery: apiData.recovery || 0,
          payment: apiData.payment || 0,
          pbalance: apiData.pbalance || 0,
          invoiceno: apiData.invoiceno || apiData.visitor_id || 'N/A',
          customer: apiData.customer || { name: 'N/A', phone: 'N/A' },
          customeraddress: apiData.customeraddress || '',
          salesman: apiData.salesman || { name: 'N/A', cnic: '' },
          payment_type: apiData.payment_type || '',
          mesg: apiData.mesg || '',
          indicator: apiData.indicator || '0',
          detail: apiData.detail || { 
            slogan_heading: '', 
            logo: '', 
            gst: '', 
            phone: '', 
            slogan_subheading: '' 
          },
          printingtime: apiData.printingtime || new Date().toLocaleTimeString(),
          manualdate: apiData.manualdate || new Date().toISOString().split('T')[0],
          totalpay: apiData.totalpay || 0,
          billnumber: apiData.billnumber || '',
          paytype: apiData.paytype || '',
          pcbalance: apiData.pcbalance || 0,
        };

        return formattedInvoiceData;
      }
    } catch (error) {
      console.error('Error fetching invoice:', error);
      toast.error('Failed to fetch invoice data');
      return null;
    }
  };

  // Handle View Invoice - Shows preview modal
  const handleViewInvoice = async (order) => {
    setInvoiceLoading(true);
    setAnimatingButton(`view-${order.id}`);
    try {
      const data = await fetchInvoiceData(order);
      if (data) {
        setInvoiceData(data);
        setShowInvoicePreview(true);
      }
    } finally {
      setInvoiceLoading(false);
      setTimeout(() => setAnimatingButton(null), 500);
    }
  };

  // Handle Print Invoice - Directly prints using PrintSaleInvoice component
  const handlePrintInvoice = async (order) => {
    setAnimatingButton(`print-${order.id}`);
    setIsPrinting(true);
    
    try {
      const data = await fetchInvoiceData(order);
      if (!data) {
        setIsPrinting(false);
        setAnimatingButton(null);
        return;
      }

      // Set the print data and trigger print
      setPrintData(data);
      setIsPrint(true);
      
    } catch (error) {
      console.error('Error printing invoice:', error);
      toast.error('Failed to print invoice');
      setIsPrinting(false);
      setAnimatingButton(null);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-PK', {
      style: 'currency',
      currency: 'PKR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const getOrderTypeIcon = (type) => {
    switch (type) {
      case 'dining':
        return <FaChair style={{ color: '#dc3545' }} />;
      case 'delivery':
        return <FaTruck style={{ color: '#007bff' }} />;
      case 'takeaway':
        return <FaRecycle style={{ color: '#28a745' }} />;
      default:
        return <FaUtensils />;
    }
  };

  const getOrderTypeLabel = (type) => {
    return type ? type.charAt(0).toUpperCase() + type.slice(1) : 'N/A';
  };

  const getStatusBadge = (status) => {
    if (status === 'completed') {
      return <span style={{ 
        background: '#d4edda', 
        color: '#155724', 
        padding: '2px 10px', 
        borderRadius: '12px',
        fontSize: '11px',
        fontWeight: '600'
      }}><FaCheckCircle /> Completed</span>;
    } else {
      return <span style={{ 
        background: '#fff3cd', 
        color: '#856404', 
        padding: '2px 10px', 
        borderRadius: '12px',
        fontSize: '11px',
        fontWeight: '600'
      }}><FaClock /> Pending</span>;
    }
  };

  // Styles
  const styles = {
    modalHeader: {
      background: 'linear-gradient(135deg, #dc3545 0%, #c0392b 100%)',
      color: 'white',
      borderRadius: '0',
      padding: '20px 25px',
    },
    modalTitle: {
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
    },
    filterContainer: {
      display: 'flex',
      gap: '10px',
      marginBottom: '20px',
      flexWrap: 'wrap',
    },
    filterBtn: {
      padding: '8px 20px',
      borderRadius: '25px',
      border: '2px solid #e0e0e0',
      background: 'white',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      fontWeight: '500',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      position: 'relative',
      overflow: 'hidden',
    },
    filterBtnActive: {
      borderColor: '#dc3545',
      background: '#fff5f5',
      color: '#dc3545',
    },
    filterBtnAnimating: {
      transform: 'scale(0.95)',
      boxShadow: '0 0 20px rgba(220, 53, 69, 0.3)',
    },
    countBadge: {
      background: '#dc3545',
      color: 'white',
      borderRadius: '50%',
      padding: '1px 8px',
      fontSize: '11px',
      marginLeft: '4px',
    },
    orderRow: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '12px 15px',
      borderBottom: '1px solid #f0f0f0',
      transition: 'all 0.3s ease',
      borderRadius: '8px',
    },
    orderInfo: {
      flex: 1,
      minWidth: 0,
    },
    orderCustomer: {
      fontWeight: '600',
      color: '#2c3e50',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      flexWrap: 'wrap',
    },
    orderMeta: {
      fontSize: '12px',
      color: '#888',
      display: 'flex',
      gap: '15px',
      flexWrap: 'wrap',
      marginTop: '2px',
    },
    actionBtn: {
      padding: '5px 12px',
      border: 'none',
      borderRadius: '6px',
      fontSize: '12px',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      display: 'flex',
      alignItems: 'center',
      gap: '4px',
      position: 'relative',
      overflow: 'hidden',
    },
    actionBtnAnimating: {
      transform: 'scale(0.9)',
      opacity: '0.7',
    },
    completeBtn: {
      background: '#28a745',
      color: 'white',
    },
    viewBtn: {
      background: '#3498db',
      color: 'white',
    },
    printBtn: {
      background: '#6c757d',
      color: 'white',
    },
    confirmContent: {
      textAlign: 'center',
      padding: '30px',
    },
    confirmIcon: {
      fontSize: '4rem',
      color: '#28a745',
      marginBottom: '15px',
    },
    confirmText: {
      fontSize: '1.1rem',
      color: '#555',
      marginBottom: '20px',
      lineHeight: '1.6',
    },
    orderSummary: {
      background: '#f8f9fa',
      padding: '15px',
      borderRadius: '8px',
      marginBottom: '20px',
    },
    summaryRow: {
      display: 'flex',
      justifyContent: 'space-between',
      padding: '4px 0',
    },
    emptyState: {
      textAlign: 'center',
      padding: '40px',
      color: '#888',
    },
    emptyIcon: {
      fontSize: '50px',
      opacity: 0.3,
    },
    loadingContainer: {
      textAlign: 'center',
      padding: '40px',
    },
    loadingText: {
      marginTop: '10px',
      color: '#888',
    },
    invoicePreviewBody: {
      padding: '20px',
      maxHeight: '80vh',
      overflowY: 'auto',
      background: '#f0f2f5',
    },
    closePreviewBtn: {
      position: 'absolute',
      top: '10px',
      right: '10px',
      background: '#dc3545',
      color: 'white',
      border: 'none',
      borderRadius: '50%',
      width: '35px',
      height: '35px',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '18px',
      transition: 'all 0.3s ease',
    },
    invoicePreviewContainer: {
      background: 'white',
      borderRadius: '12px',
      boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
      padding: '25px',
      maxWidth: '900px',
      margin: '0 auto',
    },
    summaryCard: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
      gap: '15px',
      marginBottom: '20px',
      padding: '20px',
      background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
      borderRadius: '12px',
      border: '1px solid #e0e0e0',
    },
    summaryItem: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      padding: '10px',
      background: 'white',
      borderRadius: '8px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
    },
    summaryIcon: {
      width: '40px',
      height: '40px',
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '18px',
    },
    summaryLabel: {
      fontSize: '11px',
      color: '#888',
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
    },
    summaryValue: {
      fontWeight: '600',
      fontSize: '14px',
      color: '#2c3e50',
    },
  };

  // Animation styles
  const animationStyles = `
    @keyframes spin {
      from { transform: rotate(0deg); }
      to { transform: rotate(360deg); }
    }
    
    @keyframes pulse {
      0% { box-shadow: 0 0 0 0 rgba(220, 53, 69, 0.4); }
      70% { box-shadow: 0 0 0 10px rgba(220, 53, 69, 0); }
      100% { box-shadow: 0 0 0 0 rgba(220, 53, 69, 0); }
    }
    
    @keyframes fadeIn {
      from { opacity: 0; transform: translateY(10px); }
      to { opacity: 1; transform: translateY(0); }
    }
    
    .spin-animation {
      animation: spin 1s linear infinite;
    }
    
    .pulse-animation {
      animation: pulse 1.5s ease-in-out infinite;
    }
    
    .fade-in {
      animation: fadeIn 0.3s ease forwards;
    }
    
    .modal-open {
      overflow: hidden !important;
      padding-right: 0 !important;
    }
    
    .modal {
      overflow: hidden !important;
    }
    
    .modal-dialog {
      margin: 1.75rem auto;
    }
    
    .modal-content {
      border: none;
      border-radius: 12px;
      box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
    }
    
    .modal-content,
    .modal-dialog,
    .modal-backdrop {
      animation: none !important;
      transition: none !important;
    }
    
    .modal.fade .modal-dialog {
      transition: transform 0.2s ease-out, opacity 0.2s ease-out;
    }
  `;

  return (
    <>
      {/* Inject animation styles */}
      <style>{animationStyles}</style>

      {/* Main Orders Modal */}
      <Modal 
        show={show} 
        onHide={onHide} 
        size="lg" 
        centered
        backdrop="static"
        keyboard={true}
        enforceFocus={false}
      >
        <Modal.Header closeButton style={styles.modalHeader}>
          <Modal.Title style={styles.modalTitle}>
            <FaFileInvoice /> Order Management
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: '20px' }}>
          {/* Filter Buttons */}
          <div style={styles.filterContainer}>
            <button 
              style={{
                ...styles.filterBtn,
                ...(selectedOrderType === 'dining' ? styles.filterBtnActive : {}),
                ...(animatingButton === 'dining' ? styles.filterBtnAnimating : {})
              }}
              onClick={() => handleOrderTypeFilter('dining')}
              className={animatingButton === 'dining' ? 'pulse-animation' : ''}
            >
              <FaChair /> Dining 
              <span style={styles.countBadge}>{orderCounts.dining || 0}</span>
            </button>
            <button 
              style={{
                ...styles.filterBtn,
                ...(selectedOrderType === 'delivery' ? styles.filterBtnActive : {}),
                ...(animatingButton === 'delivery' ? styles.filterBtnAnimating : {})
              }}
              onClick={() => handleOrderTypeFilter('delivery')}
              className={animatingButton === 'delivery' ? 'pulse-animation' : ''}
            >
              <FaTruck /> Delivery
              <span style={styles.countBadge}>{orderCounts.delivery || 0}</span>
            </button>
            <button 
              style={{
                ...styles.filterBtn,
                ...(selectedOrderType === 'takeaway' ? styles.filterBtnActive : {}),
                ...(animatingButton === 'takeaway' ? styles.filterBtnAnimating : {})
              }}
              onClick={() => handleOrderTypeFilter('takeaway')}
              className={animatingButton === 'takeaway' ? 'pulse-animation' : ''}
            >
              <FaRecycle /> Takeaway
              <span style={styles.countBadge}>{orderCounts.takeaway || 0}</span>
            </button>
          </div>

          {/* Orders List */}
          {ordersLoading ? (
            <div style={styles.loadingContainer}>
              <div className="spin-animation" style={{ fontSize: '40px', color: '#dc3545' }}>
                <FaSpinner />
              </div>
              <p style={styles.loadingText}>Loading orders...</p>
            </div>
          ) : orders.length === 0 ? (
            <div style={styles.emptyState}>
              <FaFileInvoice style={styles.emptyIcon} />
              <p style={{ marginTop: '10px' }}>No pending orders for {getOrderTypeLabel(selectedOrderType)}</p>
            </div>
          ) : (
            orders.map((order) => (
              <div key={order.id} style={styles.orderRow} className="fade-in">
                <div style={styles.orderInfo}>
                  <div style={styles.orderCustomer}>
                    {getOrderTypeIcon(order.order_type)} 
                    <span>{order.name || 'Unknown Customer'}</span>
                    <span>{getStatusBadge(order.order_status)}</span>
                  </div>
                  <div style={styles.orderMeta}>
                    <span>Bill: #{order.BillNumber}</span>
                    <span>Items: {order.total_items || 0}</span>
                    <span>Total: {formatCurrency(order.total)}</span>
                    <span>Salesman: {order.salesman || 'N/A'}</span>
                    {order.order_type && (
                      <span>Type: {getOrderTypeLabel(order.order_type)}</span>
                    )}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                  <button 
                    style={{
                      ...styles.actionBtn,
                      ...styles.viewBtn,
                      ...(animatingButton === `view-${order.id}` ? styles.actionBtnAnimating : {})
                    }}
                    onClick={() => handleViewInvoice(order)}
                    onMouseEnter={(e) => e.currentTarget.style.background = '#2980b9'}
                    onMouseLeave={(e) => e.currentTarget.style.background = '#3498db'}
                    disabled={invoiceLoading}
                    className={animatingButton === `view-${order.id}` ? 'pulse-animation' : ''}
                  >
                    {invoiceLoading && animatingButton === `view-${order.id}` ? (
                      <FaSpinner className="spin-animation" />
                    ) : (
                      <FaEye />
                    )}
                    View
                  </button>
                  {!order.order_status && (
                    <button 
                      style={{
                        ...styles.actionBtn,
                        ...styles.completeBtn,
                        ...(animatingButton === `complete-${order.id}` ? styles.actionBtnAnimating : {})
                      }}
                      onClick={() => handleCompleteOrder(order)}
                      onMouseEnter={(e) => e.currentTarget.style.background = '#218838'}
                      onMouseLeave={(e) => e.currentTarget.style.background = '#28a745'}
                      className={animatingButton === `complete-${order.id}` ? 'pulse-animation' : ''}
                    >
                      <FaCheckCircle /> Complete
                    </button>
                  )}
                  <button 
                    style={{
                      ...styles.actionBtn,
                      ...styles.printBtn,
                      ...(animatingButton === `print-${order.id}` ? styles.actionBtnAnimating : {})
                    }}
                    onClick={() => handlePrintInvoice(order)}
                    onMouseEnter={(e) => e.currentTarget.style.background = '#5a6268'}
                    onMouseLeave={(e) => e.currentTarget.style.background = '#6c757d'}
                    disabled={isPrinting}
                    className={animatingButton === `print-${order.id}` ? 'pulse-animation' : ''}
                  >
                    {isPrinting && animatingButton === `print-${order.id}` ? (
                      <FaSpinner className="spin-animation" />
                    ) : (
                      <FaPrint />
                    )}
                    Print
                  </button>
                </div>
              </div>
            ))
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide}>
            Close
          </Button>
          <Button 
            variant="danger" 
            onClick={() => {
              setAnimatingButton('refresh');
              fetchOrders(selectedOrderType);
              setTimeout(() => setAnimatingButton(null), 500);
            }}
            className={animatingButton === 'refresh' ? 'pulse-animation' : ''}
          >
            <FaRefresh /> Refresh
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Confirm Complete Modal */}
      <Modal 
        show={showConfirmComplete} 
        onHide={() => setShowConfirmComplete(false)} 
        centered
        backdrop="static"
        keyboard={true}
        enforceFocus={false}
      >
        <Modal.Body style={{ padding: '0' }}>
          <div style={styles.confirmContent}>
            <div style={styles.confirmIcon}>✅</div>
            <h4 style={{ marginBottom: '10px' }}>Complete Order</h4>
            <div style={styles.confirmText}>
              Are you sure you want to mark this order as completed?
            </div>
            {orderToComplete && (
              <div style={styles.orderSummary}>
                <div style={styles.summaryRow}>
                  <span>Order #</span>
                  <span><strong>{orderToComplete.BillNumber}</strong></span>
                </div>
                <div style={styles.summaryRow}>
                  <span>Customer</span>
                  <span><strong>{orderToComplete.name || 'Unknown'}</strong></span>
                </div>
                <div style={styles.summaryRow}>
                  <span>Total Amount</span>
                  <span><strong style={{ color: '#dc3545' }}>{formatCurrency(orderToComplete.total)}</strong></span>
                </div>
                <div style={styles.summaryRow}>
                  <span>Type</span>
                  <span><strong>{getOrderTypeLabel(orderToComplete.order_type)}</strong></span>
                </div>
                <div style={styles.summaryRow}>
                  <span>Salesman</span>
                  <span><strong>{orderToComplete.salesman || 'N/A'}</strong></span>
                </div>
              </div>
            )}
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
              <Button 
                variant="secondary" 
                onClick={() => setShowConfirmComplete(false)}
              >
                Cancel
              </Button>
              <Button 
                variant="success" 
                onClick={confirmCompleteOrder}
                className={animatingButton === 'confirming' ? 'pulse-animation' : ''}
                disabled={animatingButton === 'confirming'}
              >
                {animatingButton === 'confirming' ? (
                  <><FaSpinner className="spin-animation" /> Processing...</>
                ) : (
                  <><FaCheckCircle /> Confirm Complete</>
                )}
              </Button>
            </div>
          </div>
        </Modal.Body>
      </Modal>

      {/* Invoice Preview Modal */}
      <Modal 
        show={showInvoicePreview} 
        onHide={() => setShowInvoicePreview(false)} 
        size="xl"
        centered
        backdrop="static"
        keyboard={true}
        enforceFocus={false}
      >
        <Modal.Header style={styles.modalHeader}>
          <Modal.Title style={styles.modalTitle}>
            <FaFileInvoice /> Invoice Preview
            {invoiceData && (
              <span style={{
                padding: '4px 12px',
                borderRadius: '20px',
                fontSize: '12px',
                fontWeight: '500',
                background: 'rgba(255,255,255,0.2)',
                color: 'white',
                marginLeft: '10px'
              }}>
                #{invoiceData.invoiceno}
              </span>
            )}
          </Modal.Title>
          <button 
            style={styles.closePreviewBtn}
            onClick={() => setShowInvoicePreview(false)}
            onMouseEnter={(e) => e.currentTarget.style.background = '#c0392b'}
            onMouseLeave={(e) => e.currentTarget.style.background = '#dc3545'}
          >
            <FaTimes />
          </button>
        </Modal.Header>
        <Modal.Body style={styles.invoicePreviewBody}>
          {invoiceLoading ? (
            <div style={styles.loadingContainer}>
              <div className="spin-animation" style={{ fontSize: '40px', color: '#dc3545' }}>
                <FaSpinner />
              </div>
              <p style={styles.loadingText}>Loading invoice...</p>
            </div>
          ) : invoiceData ? (
            <div style={styles.invoicePreviewContainer} className="fade-in">
              {/* Invoice Summary Cards */}
              <div style={styles.summaryCard}>
                <div style={styles.summaryItem}>
                  <div style={{ ...styles.summaryIcon, background: '#fee', color: '#dc3545' }}>
                    <FaUser />
                  </div>
                  <div>
                    <div style={styles.summaryLabel}>Customer</div>
                    <div style={styles.summaryValue}>{invoiceData.customer?.name || 'N/A'}</div>
                  </div>
                </div>
                <div style={styles.summaryItem}>
                  <div style={{ ...styles.summaryIcon, background: '#e3f2fd', color: '#1976d2' }}>
                    <FaCalendarAlt />
                  </div>
                  <div>
                    <div style={styles.summaryLabel}>Date</div>
                    <div style={styles.summaryValue}>{invoiceData.manualdate || 'N/A'}</div>
                  </div>
                </div>
                <div style={styles.summaryItem}>
                  <div style={{ ...styles.summaryIcon, background: '#e8f5e9', color: '#2e7d32' }}>
                    <FaMoneyBillWave />
                  </div>
                  <div>
                    <div style={styles.summaryLabel}>Total Amount</div>
                    <div style={{ ...styles.summaryValue, color: '#dc3545', fontSize: '16px' }}>
                      {formatCurrency(invoiceData.grandtotal || 0)}
                    </div>
                  </div>
                </div>
                <div style={styles.summaryItem}>
                  <div style={{ ...styles.summaryIcon, background: '#f3e5f5', color: '#7b1fa2' }}>
                    <FaShoppingBag />
                  </div>
                  <div>
                    <div style={styles.summaryLabel}>Items</div>
                    <div style={styles.summaryValue}>{invoiceData.letter?.length || 0}</div>
                  </div>
                </div>
                {invoiceData.customer?.phone && (
                  <div style={styles.summaryItem}>
                    <div style={{ ...styles.summaryIcon, background: '#fff3e0', color: '#e65100' }}>
                      <FaPhone />
                    </div>
                    <div>
                      <div style={styles.summaryLabel}>Phone</div>
                      <div style={styles.summaryValue}>{invoiceData.customer.phone}</div>
                    </div>
                  </div>
                )}
                {invoiceData.salesman?.name && (
                  <div style={styles.summaryItem}>
                    <div style={{ ...styles.summaryIcon, background: '#e0f7fa', color: '#00695c' }}>
                      <FaTag />
                    </div>
                    <div>
                      <div style={styles.summaryLabel}>Salesman</div>
                      <div style={styles.summaryValue}>{invoiceData.salesman.name}</div>
                    </div>
                  </div>
                )}
              </div>

              {/* Invoice Print Component */}
              <div id="invoice-preview-container">
                <PrintSaleInvoice data={invoiceData} />
              </div>
            </div>
          ) : (
            <div style={styles.emptyState}>
              <FaFileInvoice style={styles.emptyIcon} />
              <p>No invoice data available</p>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowInvoicePreview(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Hidden Print Component - Same approach as AllSaleInvoices */}
      <div style={{ display: 'none' }}>
        {isPrint && printData && (
          <div id="printablearea-order">
            <PrintSaleInvoice data={printData} />
          </div>
        )}
      </div>
    </>
  );
};

export default OrderManagement;